import './app.css';

import { useEffect, useState } from 'react';
import AssetLoadProgress from './components/app/AssetLoadProgress';
import Button from './components/core/Button';
import DownloadButton from './components/app/DownloadButton';
import ErrorsPanel from './components/app/ErrorsPanel';
import FinalPreview from './components/app/FinalPreview';
import ImageSelector from './components/core/ImageSelector';
import ProfilePreview from './components/app/ProfilePreview';
import ZoomAction from './components/app/ZoomAction';

import loadImage from './utils/loadImage';

const DEFAULT_SIZE = 600;

const canvas = document.getElementById('canvas');

const App = (props) => {
  const defaultSize = { h: DEFAULT_SIZE, w: DEFAULT_SIZE };
  const defaultPosition = { x: 0, y: 0 };

  const [loadedAssetCount, setLoadedAssetCount] = useState(0);
  const [errors, setErrors] = useState([]);
  const [ratio, setRatio] = useState(0);
  const [profileImage, setProfileImage] = useState(undefined);
  const [sizes, setSizes] = useState(defaultSize);
  const [sourcePosition, setSourcePosition] = useState(defaultPosition);
  const [isRepositioning, setIsRepositioning] = useState(false);
  const [previewHeight, setPreviewHeight] = useState(300);

  const [badgeCount, setBadgeCount] = useState(-1);
  const [imageAssets, setImageAssets] = useState([]);

  const onImageSelected = (image) => {
    const { width, height } = image;
    const imageRatio =
      width > height ? DEFAULT_SIZE / width : DEFAULT_SIZE / height;

    setSizes({
      imageHeight: height,
      imageWidth: width,
      imageRatio,
    });

    setProfileImage(image);
  };

  const clearState = () => {
    setProfileImage(undefined);
    setSizes(defaultSize);
    setSourcePosition(defaultPosition);
    setRatio(0);
  };

  useEffect(() => {
    const { assetRoot } = props;

    const badgeList = Object.keys(props)
      .filter((k) => k.indexOf('badge-') > -1)
      .map((badge) => badge.replace('badge-', ''));

    setBadgeCount(badgeList.length);
    setErrors([]);

    badgeList.forEach((tour, index) => {
      const onLoad = function () {
        setLoadedAssetCount((count) => ++count);
        setImageAssets((assets) => {
          assets.push(this);
          return assets;
        });
      };

      const onError = function () {
        setLoadedAssetCount((count) => ++count);
        setErrors((messages) => [tour, ...messages]);
      };

      loadImage(`${assetRoot}/${tour}.png`, onLoad, onError);
    });
  }, [props]);

  const onRepositionStarted = () => setIsRepositioning(true);

  const onRepositionCompleted = (data) => {
    setSourcePosition(data);
    setIsRepositioning(false);
  };

  const onUpdateZoom = (value) => setRatio(value);

  const scale = 1 + ratio / 100;

  return (
    <div className="container m-auto flex w-full flex-col rounded shadow border mt-10">
      <div className="container w-full bg-green-50 p-5 text-xl rounded">
        Tour Profile Image Builder
      </div>
      <div className="container flex w-11/12 flex m-auto pt-2">
        <div
          className="w-full overflow-hidden"
          style={{
            height: `${previewHeight}px`,
          }}
        >
          <AssetLoadProgress current={loadedAssetCount} total={badgeCount} />
          <ImageSelector
            visible={
              badgeCount > -1 && badgeCount <= loadedAssetCount && !profileImage
            }
            onImageSelected={onImageSelected}
          />

          {profileImage && (
            <div className="m-auto max-w-md overflow-hidden">
              <ProfilePreview
                previewHeight={previewHeight}
                scale={scale}
                isVisible={isRepositioning}
                onRepositionStarted={onRepositionStarted}
                onRepositionCompleted={onRepositionCompleted}
                sizes={sizes}
                profileHeight={sizes.imageHeight}
                profileWidth={sizes.imageWidth}
                profileImage={profileImage}
                canvasSize={DEFAULT_SIZE}
              />
              <FinalPreview
                isHidden={!isRepositioning}
                onSizeUpdate={setPreviewHeight}
                sizes={sizes}
                sourcePosition={sourcePosition}
                profileImage={profileImage}
                imageAssets={imageAssets}
                scale={scale}
                canvasSize={DEFAULT_SIZE}
              />
            </div>
          )}
        </div>
      </div>
      {profileImage && (
        <div className="container m-auto flex max-w-md flex-col w-11/12 pb-5">
          <div className="m-auto w-full p-5">
            <ZoomAction value={`${ratio}`} onUpdate={onUpdateZoom} />
          </div>
          <div className="flex justify-center  m-auto w-full">
            <Button onClick={clearState}>Restart</Button>
            <DownloadButton imageUrl={canvas?.toDataURL()} />
          </div>
        </div>
      )}
      <ErrorsPanel errors={errors} />
      <div className="container m-auto w-full bg-green-50 p-2 rounded" />
    </div>
  );
};

export default App;
